/**
 * Usage bar. Screen spec 4.4.
 *
 * THE BAR IS DECORATION. THE NUMBER IS THE CONTENT. At 1.4x string expansion
 * the label wraps and the bar shrinks, never the other way around, which is
 * why the value sits in its own column and the track takes whatever is left.
 *
 * Zero is a real state with its own words, not an empty track. An empty bar
 * says "something is wrong with this widget". "No traffic yet" says the true
 * thing, which is that nobody has visited yet.
 *
 * Over the limit draws the fill to the end, adds a hatch band past the end of
 * the track, and writes the overage in words. A bar that simply sits full
 * cannot say how far past the line you are.
 *
 * Deviation from screen spec 4.4, recorded: the track is 4px, not 8px. Eight
 * pixels of colored bar is a heavier chromatic object than anything else on an
 * Instrument screen and it starts to compete with the lamps, which are the
 * only things allowed to pull the eye. Four pixels still reads as a measure.
 */

import { bytes, count, percent, percentLabel } from '../../lib/format';
import { t } from '../../lib/i18n/t';
import type { UsageMetric } from '../../lib/types';
import '../../styles/components/usage-bar.css';

/** The threshold flagged for product sign off. On screen so it is arguable. */
export const NEAR_LIMIT_AT = 80;

export type UsageTone = 'none' | 'ok' | 'near' | 'over';

export function usageTone(used: number, limit: number): UsageTone {
  if (used <= 0) return 'none';
  const pct = percent(used, limit);
  if (pct >= 100) return 'over';
  if (pct >= NEAR_LIMIT_AT) return 'near';
  return 'ok';
}

/** A meter's unit: a usage metric's, or US cents for the credits bar (PlanSummary). */
export type UsageBarUnit = UsageMetric['unit'] | 'usd_cents';

function amount(value: number, unit: UsageBarUnit): string {
  if (unit === 'usd_cents') return `$${(value / 100).toFixed(2)}`;
  if (unit === 'bytes') return bytes(value);
  if (unit === 'minutes') return t('{n} min', { n: count(value) });
  return count(value);
}

/** One part of a meter's fill, for a bar that says who spent it. */
export interface UsageSegment {
  key: string;
  label: string;
  used: number;
}

/**
 * A part's share of what was spent, whole percent. A part that spent
 * something never reads "0%": that says it spent nothing, which is false.
 */
function sharePercent(used: number, spent: number): string {
  const pct = percent(used, spent);
  return pct > 0 && pct < 0.5 ? '<1%' : `${percentLabel(used, spent, 0)}%`;
}

export interface UsageBarProps {
  label: string;
  used: number;
  limit: number;
  unit?: UsageBarUnit;
  /** The 6px variant, for the account menu and the table footers. */
  compact?: boolean;
  /** Renders the reset line. Omit where the period is stated elsewhere. */
  resetsInDays?: number;
  /**
   * Puts "1.24% used" in the head row, where a bytes meter puts "4.2 MB /
   * 1.0 GB". On for the compute meter, whose value is an abstract unit count
   * that means nothing on its own; off for bytes, where the amount is already
   * the reading a person wants.
   *
   * It used to print under the track instead, which put the one number a
   * compute meter has on a different line from the one number every other
   * meter has, and left the head row of that meter empty. Same row, same
   * place, whatever the unit.
   *
   * Off with `percentOnly` on is a meter that states no reading at all, and
   * that is deliberate: the usage screen prints the percentage in its own
   * column beside the bar, aligned across all three rows, and a second copy
   * in the head would be the same number twice.
   */
  showPercent?: boolean;
  /**
   * Drops the "used / limit" reading and leaves the percentage to carry the
   * meter on its own.
   *
   * On for compute, and the reason is the reason compute exists: the unit is
   * an abstraction over requests and processing time, so "87 / 150,000" is
   * two numbers a person cannot act on and cannot check, printed where the
   * one number that means something already is. A quota reads as a
   * percentage. Bytes do not take this: "958 MB of 5.0 GB" is a reading
   * somebody can do something about.
   *
   * Zero keeps its sentence. "No traffic yet" is not an amount, and it is
   * still the true thing to say about an app nobody has visited.
   */
  percentOnly?: boolean;
  /**
   * Prints "0 of 150,000" at zero instead of "No traffic yet".
   *
   * "No traffic yet" is the right sentence for an app that is running and
   * quiet. It is the wrong one for an account that owns nothing yet and is
   * being shown what its plan gives it: there the ceiling IS the content, and
   * a track with no number on it says the opposite of what the screen means.
   */
  showLimitAtZero?: boolean;
  /**
   * Splits the fill into parts, biggest first, with a key under the track
   * naming each part and its share of what was SPENT, not of the allowance.
   * The head already states the allowance reading; on a generous plan every
   * part's share of the allowance rounds to 0.00%, which says nothing about
   * who spent it, and that is the one question the key exists to answer.
   *
   * The parts are shades of the meter's own tone rather than a palette of
   * their own. The tone is the reading that matters (is this account near its
   * line); the split is the explanation underneath it, and a row of distinct
   * colours would out-shout the one colour on the bar that means something.
   * Each key entry prints its percentage, so the shade only has to say which
   * line of the key it belongs to.
   */
  segments?: UsageSegment[];
  /**
   * The zero state's sentence. "No traffic yet" is the true thing to say about
   * an app; the credits bar says something about money instead.
   */
  zeroSentence?: string;
}

export function UsageBar({
  label,
  used,
  limit,
  unit = 'count',
  compact = false,
  resetsInDays,
  showPercent = false,
  showLimitAtZero = false,
  percentOnly = false,
  segments,
  zeroSentence,
}: UsageBarProps) {
  const tone = usageTone(used, limit);
  const pct = percent(used, limit);
  // Exactly at the limit is the red case but it is not the overage case.
  // "Over the limit by 0 min" is a sentence that makes a person doubt the
  // number rather than believe it.
  const past = used > limit;

  // A key is only worth drawing once something has been spent: at zero the
  // bar already says "No traffic yet", and a key of nothing contradicts it.
  const parts = (segments ?? []).filter((s) => s.used > 0);
  const showSegments = tone !== 'none' && parts.length > 0;
  const spent = parts.reduce((n, part) => n + part.used, 0);

  // Zero says its sentence unless this bar is the one being asked to state a
  // ceiling (an account with no apps yet), and a percent-only meter has no
  // ceiling to state.
  const sentence = tone === 'none' && !(showLimitAtZero && !percentOnly);
  const value = sentence
    ? (zeroSentence ?? t('No traffic yet'))
    : percentOnly
      ? showPercent
        ? t('{n}% used', { n: percentLabel(used, limit) })
        : null
      : `${amount(used, unit)} / ${amount(limit, unit)}`;

  return (
    <div className="usage" data-tone={tone} data-compact={compact ? 'yes' : 'no'}>
      <div className="usage__head">
        <span className="usage__label">{label}</span>
        {/* Mono is reserved for a copyable literal. The zero state's value is
            a sentence, not a measurement, so it drops the mono flag rather
            than setting prose in the machine face. */}
        {value === null ? null : (
          <span className={sentence ? 'usage__value' : 'usage__value mono'}>{value}</span>
        )}
      </div>

      <div className="usage__track-row">
        <div
          className="usage__track"
          role="meter"
          aria-valuenow={Number(Math.min(pct, 100).toFixed(2))}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('{label} used', { label })}
        >
          {/* The fill is declared at its value and a keyframe walks it up from
              zero once, on mount. Doing it in CSS rather than with a mounted
              flag means the bar is correct in the first painted frame even
              when no animation frame ever runs, which is the case in a
              screenshot pass and in a print. */}
          <span
            className="usage__fill"
            data-segmented={showSegments ? 'yes' : 'no'}
            style={{ inlineSize: `${Math.min(pct, 100)}%` }}
          >
            {showSegments
              ? parts.map((part, index) => (
                  <span
                    key={part.key}
                    className="usage__seg"
                    data-seg={index}
                    style={{ flexGrow: part.used }}
                  />
                ))
              : null}
          </span>
        </div>
        {past ? <span className="usage__hatch" aria-hidden="true" /> : null}
      </div>

      {showSegments ? (
        <ul className="usage__key" aria-label={t('{label} by project', { label })}>
          {parts.map((part, index) => (
            <li key={part.key} className="usage__key-item">
              <span className="usage__swatch" data-seg={index} aria-hidden="true" />
              <span className="usage__key-label">{part.label}</span>
              <span className="usage__key-value mono">
                {sharePercent(part.used, spent)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* An overage in an abstract unit is the same unreadable number as the
          amount above it, and the percentage past 100 already says how far
          over the line this is. */}
      {past && !percentOnly ? (
        <p className="usage__over">
          {t('Over the limit by {amount}', { amount: amount(used - limit, unit) })}
        </p>
      ) : null}

      {resetsInDays !== undefined ? (
        <p className="usage__resets">
          {resetsInDays === 0
            ? t('Resets today')
            : resetsInDays === 1
              ? t('Resets tomorrow')
              : t('Resets in {n} days', { n: resetsInDays })}
        </p>
      ) : null}
    </div>
  );
}
